/**
 * The alarm.
 *
 * ---------------------------------------------------------------------------
 * THE ONLY REQUIREMENT THAT MATTERS
 * ---------------------------------------------------------------------------
 * Someone alone at home who feels unsafe must be able to raise this without
 * thinking. That rules out: a confirmation dialog with a "Are you sure?", a
 * form, a reason picker, and anything that needs two hands. What is left is a
 * press-and-hold - long enough not to fire in a pocket, short enough to be
 * over before you have finished deciding - and then it is sent.
 *
 * The hold is the *only* gate. Everything else the alert needs (where you are,
 * which booking, who is with you) is snapshotted from what the app already
 * knows, because none of it can be asked for at that moment.
 *
 * ---------------------------------------------------------------------------
 * A FALSE ALARM COSTS NOTHING
 * ---------------------------------------------------------------------------
 * `false_alarm` is a normal ending, not an admission. Nothing about it is
 * counted, rate-limited or shown back to the customer later. Hesitating to
 * press the button because of what happens if you are wrong is the failure
 * this feature exists to prevent, so the screen says so out loud.
 */

import { Booking } from './bookings';

/**
 * How long the button must be held.
 *
 * 1.6 seconds: past the accidental brush, short of feeling like a fight with
 * the phone. The ring fills over exactly this, so the wait is visible rather
 * than a button that ignores you.
 */
export const SOS_HOLD_MS = 1600;

export type SosStatus = 'active' | 'false_alarm' | 'closed';

/**
 * What is sent, and what the screen shows back.
 *
 * The snapshot is copied at the moment of raising rather than referenced live:
 * the point of it is what the situation was when the alarm went off, and a
 * booking that advances a stage afterwards must not rewrite that.
 */
export type SosAlert = {
  id: string;
  status: SosStatus;
  raisedAt: string;
  /** Where the app last knew the customer to be. */
  address: string;
  coords?: { latitude: number; longitude: number };
  bookingRef?: string;
  service?: string;
  professional?: string;
};

/** Everything an alert carries about a booking, taken in one go. */
export const contextFor = (booking?: Booking): Partial<SosAlert> =>
  booking
    ? {
        bookingRef: booking.reference,
        service: booking.title,
        address: booking.address,
        professional: booking.professional?.name,
      }
    : {};

/**
 * Who is called, and in what order.
 *
 * Ops first and always: they have the booking, the professional's number and
 * the authority to act, and they can reach the emergency services faster than
 * a customer explaining an address down a bad line. The 112 row is second and
 * never hidden - if the platform is the problem, the platform is not the
 * number to ring.
 */
export const sosChannels: Array<{
  key: string;
  label: string;
  detail: string;
  /** `tel:` handled by the screen; ops is an in-app dispatch. */
  phone?: string;
}> = [
  {
    key: 'ops',
    label: 'Homingo safety team',
    detail: 'Alerted the moment you raise this. They can see your booking.',
  },
  {
    key: 'police',
    label: 'Emergency services (112)',
    detail: 'Call directly if you are in immediate danger.',
    phone: '112',
  },
];
